import { CircularProgress, MenuItem, Stack, TextField, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { warehousesApi, type WarehouseType } from "@/api/endpoints";

/**
 * Warehouse selector. A dropdown rather than a search box: a hospital has tens of warehouses, not
 * thousands, and seeing the whole list is how staff confirm they picked the right store.
 *
 * Inactive warehouses are hidden — except the one already selected, so an existing document keeps
 * showing where its stock came from rather than silently emptying the field.
 */
export function WarehousePicker({
  value, onChange, label = "Warehouse", required, disabled, helperText, error, type, sx,
}: {
  value: string;
  onChange: (warehouseId: string) => void;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  helperText?: string;
  error?: boolean;
  /** Restrict to one warehouse type (cold chain, quarantine…). Omit for all. */
  type?: WarehouseType;
  sx?: object;
}) {
  const q = useQuery({
    queryKey: ["warehouses-picker", type ?? "all"],
    queryFn: () => warehousesApi.list({ type, pageSize: 200 }),
    staleTime: 5 * 60_000,
  });

  const all = q.data?.items ?? [];
  const options = all.filter(w => w.isActive || w.id === value);

  return (
    <TextField
      select size="small" sx={sx} disabled={disabled || q.isLoading}
      label={`${label}${required ? " *" : ""}`}
      value={options.some(w => w.id === value) ? value : ""}
      onChange={(e) => onChange(e.target.value)}
      error={error}
      helperText={helperText ?? (q.isError ? "Could not load warehouses" : undefined)}
      InputProps={q.isLoading ? { endAdornment: <CircularProgress size={16} /> } : undefined}
    >
      <MenuItem value=""><em>— pick warehouse —</em></MenuItem>
      {options.map(w => (
        <MenuItem key={w.id} value={w.id}>
          <Stack spacing={0} sx={{ minWidth: 0 }}>
            <Typography variant="body2">{w.code} — {w.name}</Typography>
            <Typography variant="caption" color="text.secondary">
              {w.branchName}{w.isActive ? "" : " · inactive"}
            </Typography>
          </Stack>
        </MenuItem>
      ))}
    </TextField>
  );
}
